import type { Profile, Stats, Track, TrackResponse, MutationResponse, Rating, RatingStatus } from './types.js';
import { getAuthHeaders } from './auth.js';

export async function fetchUsers(): Promise<{ users: string[]; activeUser: string | null; currentUser: Profile | null }> {
  const res = await fetch('/api/users', {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function fetchRandomTrack(_user: string | null, localOnly = true): Promise<TrackResponse> {
  const res = await fetch(`/api/random-track?localOnly=${localOnly}`, {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function fetchRandomLikedTrack(_user: string | null): Promise<TrackResponse> {
  const res = await fetch('/api/random-liked-track', {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function fetchLikedTracks(_user: string | null): Promise<Track[]> {
  const res = await fetch('/api/liked-tracks', {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function rateTrack(_user: string | null, videoId: string, status: RatingStatus, title: string | null = null, artist: string | null = null): Promise<MutationResponse> {
  const res = await fetch('/api/rate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ videoId, status, title, artist })
  });
  return await res.json();
}

export async function resetRating(_user: string | null, videoId: string): Promise<MutationResponse> {
  const res = await fetch('/api/reset-rating', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ videoId })
  });
  return await res.json();
}

export async function fetchStats(_user: string | null): Promise<Stats> {
  const res = await fetch('/api/stats', {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function fetchRatings(_user: string | null, status: RatingStatus = 'like'): Promise<Rating[]> {
  const res = await fetch(`/api/ratings?status=${status}`, {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function searchTracks(_user: string | null, query: string): Promise<Track[]> {
  const res = await fetch(`/api/search?q=${encodeURIComponent(query)}`, {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function fetchTrackById(_user: string | null, videoId: string): Promise<TrackResponse> {
  const res = await fetch(`/api/track/${encodeURIComponent(videoId)}`, {
    headers: { ...getAuthHeaders() }
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return await res.json();
}

export async function addYouTubeLink(_user: string | null, url: string, target: 'tracks' | 'playlists' = 'tracks'): Promise<MutationResponse> {
  const res = await fetch('/api/add-link', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ url, target })
  });
  return await res.json();
}

export async function nukeTrack(_user: string | null, videoId: string, force = false): Promise<MutationResponse> {
  const res = await fetch('/api/nuke-track', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ videoId, force })
  });
  return await res.json();
}
